class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('click', this.handleSaveForLaterClick.bind(this));
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.addEventListener('click', this.handleOutsideClick.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  handleOutsideClick(event) {
    if (!this.classList.contains('active')) return;

    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner && event.target instanceof Node && drawerInner.contains(event.target)) return;

    this.close();
  }

  handleSaveForLaterClick(event) {
    const saveLink = event.target.closest('.cart-item__save-later');
    if (!saveLink) return;

    event.preventDefault();

    const productData = {
      title: saveLink.dataset.productTitle || '',
      image: saveLink.dataset.productImg || '',
      price: saveLink.dataset.productPrice || '',
      url: saveLink.dataset.productUrl || ''
    };

    this.addToWishlist(productData);

    const cartItems = saveLink.closest('cart-drawer-items');
    const lineIndex = parseInt(saveLink.dataset.lineIndex || '', 10);
    const variantId = saveLink.dataset.variantId || undefined;

    if (cartItems && Number.isFinite(lineIndex) && lineIndex > 0) {
      cartItems.updateQuantity(lineIndex, 0, event, 'updates[]', variantId);
    } else {
      window.location.href = '/pages/wishlist-1';
    }
  }

  addToWishlist(productData) {
    const storageKey = 'onhow-wishlist';
    let wishlist = [];
    try {
      wishlist = JSON.parse(localStorage.getItem(storageKey) || '[]');
      if (!Array.isArray(wishlist)) wishlist = [];
    } catch (error) {
      wishlist = [];
    }
    const exists = wishlist.some(
      (item) => item.title === productData.title && item.url === productData.url
    );

    if (!exists) {
      wishlist.push(productData);
      localStorage.setItem(storageKey, JSON.stringify(wishlist));
    }

    this.updateWishlistCount(wishlist.length);
  }

  updateWishlistCount(count) {
    document.querySelectorAll('.onhow-wishlist-count').forEach((el) => {
      el.textContent = count;
      el.style.display = count > 0 ? 'flex' : 'none';
    });

    if (window.onhowWishlist && typeof window.onhowWishlist.updateWishlistCount === 'function') {
      window.onhowWishlist.updateWishlistCount();
    }
  }

  setHeaderCartIconAccessibility() {
    const setupCartIcon = (cartLink) => {
      if (!cartLink) return;
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    };

    setupCartIcon(document.querySelector('#cart-icon-bubble'));
    setupCartIcon(document.querySelector('#cart-icon-bubble-mobile'));
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    document.body.classList.add('cart-drawer-open');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    document.body.classList.remove('cart-drawer-open');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    const sections = [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
    if (document.getElementById('cart-icon-bubble-mobile')) {
      sections.push({
        id: 'cart-icon-bubble-mobile',
        section: 'cart-icon-bubble-mobile',
        selector: '.shopify-section',
      });
    }
    return sections;
  }

  async refreshDrawerFromServer() {
    const url = `${window.routes?.cart_url || '/cart'}?section_id=cart-drawer`;
    const response = await fetch(url);
    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const sectionHtml = doc.querySelector('[id^="shopify-section"]') || doc.querySelector('cart-drawer') || doc.querySelector('#CartDrawer');
    const newDrawerInner = sectionHtml?.querySelector('.drawer__inner') || doc.querySelector('.drawer__inner');
    const currentDrawerInner = document.querySelector('#CartDrawer .drawer__inner');
    if (newDrawerInner && currentDrawerInner) {
      currentDrawerInner.innerHTML = newDrawerInner.innerHTML;
    }
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      cartDrawer.classList.toggle('is-empty', !doc.querySelector('.cart-item'));
    }
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
